/**
 * AI 实时辅助通话入口
 *
 * 当 TCCC 外呼通知栏浮层出现时，在其正下方注入一个入口条，
 * 点击后在新标签页打开 /ai-calling/:customerId 页面。
 *
 * customerId 来源：当前所在的客户详情页 /customer/:customerId。
 * 若能解析出 customerId（如客户详情页外呼）则携带跳转；
 * 若无客户上下文（如坐席工作台 /task/workspace 手输号码外呼）则跳转无参形式，
 * 由 AI 辅助页降级为不展示客户名。两种场景由同一套显示逻辑与权限点控制。
 *
 * 位置与宽度实时跟随 TCCC 浮层（浮层为 SDK 注入的 fixed 定位 DOM，
 * 因此这里同样用 fixed 定位对齐）。浮层消失时自动隐藏。
 */
import React from 'react'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { globalStore } from '@store'
import { useTcccCallBarRect } from './useTcccCallBarRect'
import './style.less'

/** 入口条与浮层底部的间距（px），负值让入口条上移遮住浮层底部圆角，实现无缝衔接 */
const GAP = -2
/** 入口条高度（px） */
const ENTRY_HEIGHT = 40

/** AI 实时辅助通话页路径前缀 */
const AI_CALLING_PATH_PREFIX = '/ai-calling'
/** 客户详情页路径（customerId 来源） */
const CUSTOMER_DETAIL_PATH = '/customer/:customerId'

const AiCallingEntry: React.FC = () => {
  const rect = useTcccCallBarRect()
  const location = useLocation()
  // 从当前客户详情页路由中解析 customerId（工作台等无客户上下文的页面解析不到，为 undefined）
  const match = useRouteMatch<{ customerId: string }>(CUSTOMER_DETAIL_PATH)
  const customerId = match?.params.customerId
  // AI 实时通话辅助功能灰度：无权限点时不展示入口
  const hasGrayPermission = globalStore(s => s.authPointMap.callingFeatureGray?.result || false)

  // 无灰度权限 / 已在 AI 实时辅助通话页 / 浮层未出现时，不展示入口
  // 注意：不再要求必须存在 customerId —— 无客户上下文时仍展示入口并跳转无参形式
  if (!hasGrayPermission || location.pathname.startsWith(AI_CALLING_PATH_PREFIX) || !rect) return null

  const style: React.CSSProperties = {
    position: 'fixed',
    top: rect.top + rect.height + GAP,
    left: rect.left,
    width: rect.width,
    height: ENTRY_HEIGHT,
  }

  // 有客户上下文时携带 customerId，否则跳转无参形式（AI 辅助页降级为不展示客户名）
  const href = customerId ? `${AI_CALLING_PATH_PREFIX}/${customerId}` : AI_CALLING_PATH_PREFIX

  return (
    <a
      className="ai-calling-entry"
      href={href}
      style={style}
      target="_blank"
      rel="noopener noreferrer"
      role="button"
      aria-label="进入 AI 实时辅助通话"
    >
      <span className="ai-calling-entry__icon" aria-hidden="true">✦</span>
      <span className="ai-calling-entry__text">AI实时辅助通话</span>
      <span className="ai-calling-entry__arrow" aria-hidden="true">›</span>
    </a>
  )
}

export default AiCallingEntry
